/**
 * TeacherDayPlanner — day navigator for Flight Control. Move to any date (ahead or
 * back), see that day's Flight Plan, inject per-grade content that feeds the
 * worksheets, and generate / preview each grade's daily worksheet (with answer key).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays, Loader2, Save, RefreshCw, Eye, FileText } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fromISO = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const prettyDate = (s) => fromISO(s).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

const TeacherDayPlanner = () => {
  const [dateISO, setDateISO] = useState(toISO(new Date()));
  const [loading, setLoading] = useState(true);
  const [day, setDay] = useState(null);
  const [edits, setEdits] = useState({});      // grade -> {focus, notes}
  const [busy, setBusy] = useState(null);       // "save-6" | "gen-6" | "prev-6"
  const [preview, setPreview] = useState({});   // grade -> worksheet

  const load = useCallback(async (iso) => {
    setLoading(true);
    try {
      const r = await api.get(`/academy/day?date=${iso}`, { headers: authHeaders() });
      setDay(r.data);
      const e = {};
      (r.data.grades || []).forEach((g) => { e[g.grade] = { ...g.teacher_content }; });
      setEdits(e);
      setPreview({});
    } catch (err) { toast.error('Could not load day'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(dateISO); }, [dateISO, load]);

  const shift = (days) => { const d = fromISO(dateISO); d.setDate(d.getDate() + days); setDateISO(toISO(d)); };

  const saveContent = async (grade) => {
    setBusy(`save-${grade}`);
    try {
      const { focus = '', notes = '' } = edits[grade] || {};
      await api.post('/academy/daily-content', { date: dateISO, grade, focus, notes }, { headers: authHeaders() });
      toast.success(`Grade ${grade} content saved — worksheet rebuilding`);
      setTimeout(() => load(dateISO), 800);
    } catch { toast.error('Could not save'); }
    finally { setBusy(null); }
  };

  const generate = async (grade, force) => {
    setBusy(`gen-${grade}`);
    try {
      await api.post(`/academy/daily-worksheet/${grade}/generate?date=${dateISO}&force=${force ? 'true' : 'false'}`, {}, { headers: authHeaders() });
      toast.success(`Grade ${grade} worksheet generating — refresh in ~1 min`);
    } catch { toast.error('Could not generate'); }
    finally { setBusy(null); }
  };

  const doPreview = async (grade) => {
    if (preview[grade]) { setPreview((p) => ({ ...p, [grade]: null })); return; }
    setBusy(`prev-${grade}`);
    try {
      const r = await api.get(`/academy/daily-worksheet?date=${dateISO}&grade=${grade}`, { headers: authHeaders() });
      setPreview((p) => ({ ...p, [grade]: r.data.worksheet }));
    } catch { toast.error('Could not load worksheet'); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid="teacher-day-planner">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-emerald-400" />
          <h3 className="text-sm font-black tracking-[0.2em] uppercase">Lesson Planner · Day Navigator</h3>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} className="p-2 border-2 border-zinc-800 text-zinc-400 hover:border-emerald-500 hover:text-white transition-colors" data-testid="day-prev"><ChevronLeft className="w-4 h-4" /></button>
          <input type="date" value={dateISO} onChange={(e) => e.target.value && setDateISO(e.target.value)} className="bg-black border-2 border-zinc-800 text-zinc-200 text-xs font-mono px-3 py-2 outline-none focus:border-emerald-500" data-testid="day-date-input" />
          <button onClick={() => shift(1)} className="p-2 border-2 border-zinc-800 text-zinc-400 hover:border-emerald-500 hover:text-white transition-colors" data-testid="day-next"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      {loading ? (
        <div className="border-2 border-zinc-800 px-5 py-6 flex items-center gap-3"><Loader2 className="w-4 h-4 animate-spin text-emerald-400" /><span className="text-xs text-zinc-500">Loading {prettyDate(dateISO)}…</span></div>
      ) : day ? (
        <>
          <div className="border-2 border-emerald-500/30 bg-emerald-500/5 px-4 py-3 mb-4" data-testid="day-plan-summary">
            <div className="text-[10px] tracking-[0.3em] uppercase text-emerald-400 font-black">{prettyDate(dateISO)} · Focus: {day.plan?.subject}</div>
            <p className="text-sm text-zinc-200 mt-1 leading-snug">{day.plan?.daily_theme}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {(day.grades || []).map((g) => (
              <div key={g.grade} className="border-2 border-zinc-800 p-4" data-testid={`day-grade-${g.grade}`}>
                <div className="flex items-center justify-between">
                  <div className="text-base font-black text-white">Grade {g.grade}</div>
                  <div className="text-[10px] font-mono">
                    {g.has_worksheet
                      ? <span className="text-emerald-400">✓ {g.question_count} Q{g.submissions ? ` · ${g.submissions} sub` : ''}</span>
                      : <span className="text-zinc-600">no worksheet yet</span>}
                  </div>
                </div>

                <label className="block text-[9px] uppercase tracking-widest text-zinc-500 font-black mt-3 mb-1">Focus (adds to worksheet)</label>
                <input
                  value={edits[g.grade]?.focus || ''}
                  onChange={(e) => setEdits((s) => ({ ...s, [g.grade]: { ...s[g.grade], focus: e.target.value } }))}
                  placeholder="e.g. Connect proportions to shooting %"
                  className="w-full bg-black border-2 border-zinc-800 px-2.5 py-2 text-xs text-zinc-200 outline-none focus:border-emerald-500"
                  data-testid={`day-focus-${g.grade}`}
                />
                <label className="block text-[9px] uppercase tracking-widest text-zinc-500 font-black mt-2 mb-1">Notes / content</label>
                <textarea
                  value={edits[g.grade]?.notes || ''}
                  onChange={(e) => setEdits((s) => ({ ...s, [g.grade]: { ...s[g.grade], notes: e.target.value } }))}
                  rows={2}
                  placeholder="Extra context, vocabulary, or problems to weave in…"
                  className="w-full bg-black border-2 border-zinc-800 px-2.5 py-2 text-xs text-zinc-200 outline-none focus:border-emerald-500"
                  data-testid={`day-notes-${g.grade}`}
                />

                <div className="flex flex-wrap gap-2 mt-3">
                  <button onClick={() => saveContent(g.grade)} disabled={busy === `save-${g.grade}`} className="flex items-center gap-1.5 border-2 border-emerald-600 text-emerald-300 hover:bg-emerald-500 hover:text-black px-3 py-1.5 text-[9px] font-black tracking-[0.2em] uppercase transition-colors" data-testid={`day-save-${g.grade}`}>
                    {busy === `save-${g.grade}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save &amp; Rebuild
                  </button>
                  <button onClick={() => generate(g.grade, g.has_worksheet)} disabled={busy === `gen-${g.grade}`} className="flex items-center gap-1.5 border-2 border-zinc-800 text-zinc-400 hover:border-emerald-500 hover:text-white px-3 py-1.5 text-[9px] font-black tracking-[0.2em] uppercase transition-colors" data-testid={`day-generate-${g.grade}`}>
                    {busy === `gen-${g.grade}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} {g.has_worksheet ? 'Regen' : 'Generate'}
                  </button>
                  {g.has_worksheet && (
                    <button onClick={() => doPreview(g.grade)} disabled={busy === `prev-${g.grade}`} className="flex items-center gap-1.5 border-2 border-zinc-800 text-zinc-400 hover:border-emerald-500 hover:text-white px-3 py-1.5 text-[9px] font-black tracking-[0.2em] uppercase transition-colors" data-testid={`day-preview-${g.grade}`}>
                      {busy === `prev-${g.grade}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />} {preview[g.grade] ? 'Hide' : 'Preview'}
                    </button>
                  )}
                </div>

                {preview[g.grade] && (
                  <div className="mt-3 border-t border-zinc-800 pt-3 space-y-2" data-testid={`day-preview-body-${g.grade}`}>
                    <div className="flex items-center gap-1.5 text-[10px] text-zinc-400"><FileText className="w-3 h-3" /> {preview[g.grade].title} · {preview[g.grade].standard_code}</div>
                    {(preview[g.grade].questions || []).map((q, i) => (
                      <div key={q.id} className="text-[11px] text-zinc-300 leading-snug">
                        <span className="font-black text-emerald-400">{i + 1}. [{q.type}]</span> {q.prompt}
                        {q.options && <span className="block text-zinc-500 pl-3">{q.options.join('  ')}</span>}
                        <span className="block text-emerald-500/80 pl-3">Key: {q.answer}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
};

export default TeacherDayPlanner;
